import React, { useState, useEffect } from "react";
import { X, Send, AlertTriangle, Mail } from "lucide-react";
import Swal2 from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./app.css";
import Loading from "./loading";
import { secretKey } from "./data";

const MySwal = withReactContent(Swal2);
const API = "https://apiftpsplit.ftpeople.pl";
const hdrs = { "Content-Type": "application/json", Authorization: secretKey };

const fmtPLN = (n) =>
  Number(n || 0).toLocaleString("pl-PL", { minimumFractionDigits: 0 }) + " zł";

const SendLeadsDetails = (props) => {
  const [loading, setLoading] = useState(true);
  const [sendTable, setSendTable] = useState([]);
  const [mailTable, setMailTable] = useState([]);
  const [sendSum, setSendSum] = useState(0);
  const [sendMail, setSendMail] = useState(true);
  const [statuses, setStatuses] = useState([]);

  /* ── init ── */
  useEffect(() => {
    (async () => {
      try {
        /* check duplicates */
        const checkRes = await fetch(`${API}/checkLead`, {
          method: "POST",
          headers: hdrs,
          body: JSON.stringify({ leads: props.leads, clients: props.clients }),
        });
        const dupes = await checkRes.json();

        /* build send table */
        let sum = 0;
        const table = props.clients.map((client) => {
          const validLeads = props.leads.filter(
            (lead) =>
              !dupes.some(
                (d) => d.mail === lead.mail && +d.companyId === +client.id
              )
          );
          const dupLeads = props.leads
            .filter((lead) =>
              dupes.some(
                (d) => d.mail === lead.mail && +d.companyId === +client.id
              )
            )
            .map((lead) => ({
              lead,
              assDate: dupes.find((d) => d.mail === lead.mail)?.assDate || "",
            }));

          sum += client.price * validLeads.length;
          return {
            id: client.id,
            name: client.name,
            mail: client.mail,
            price: client.price,
            leads: validLeads,
            leadsNon: dupLeads,
          };
        });

        setSendTable(table);
        setSendSum(sum);

        /* build mail table */
        const mails = props.leads
          .filter((l) => l.mail)
          .map((lead) => ({
            mail: lead.mail,
            name: lead.name,
            type: props.sendOption,
            companies: table
              .filter(
                (c) =>
                  c.leads.some((l) => l.id === lead.id) &&
                  !c.name.includes("FTP")
              )
              .map((c) => c.name),
          }));
        setMailTable(mails);

        /* fetch statuses */
        const stRes = await fetch(`${API}/getStatuses`, {
          method: "POST",
          headers: hdrs,
          body: "{}",
        });
        setStatuses(await stRes.json());
      } catch (err) {
        console.error("SendLeadsDetails init:", err);
      }
      setLoading(false);
    })();
  }, []);

  /* ── send ── */
  const handleSend = () => {
    const model = {};
    statuses.forEach((s) => {
      model[s.name] = s.name;
    });

    MySwal.fire({
      title: "Wybierz status",
      input: "select",
      inputOptions: model,
      inputPlaceholder: "wybierz wartość",
      showCancelButton: true,
      cancelButtonText: "Anuluj",
      inputValidator: (v) =>
        new Promise((res) => res(v ? undefined : "musisz wybrać wartość")),
    }).then(async (result) => {
      if (result.isConfirmed) {
        fetch(`${API}/sendLeads`, {
          method: "POST",
          headers: hdrs,
          body: JSON.stringify({
            mailTable,
            sendTable,
            type: props.sendOption,
            status: result.value,
            send: sendMail,
          }),
        }).catch(() => {});
        MySwal.fire({
          icon: "success",
          title: "Wysłano",
          text: "kontakty",
        }).then(() => props.close());
      }
    });
  };

  const hasLeadsToSend = sendTable.some((c) => c.leads.length > 0);

  return (
    <div
      className="sn-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.close();
      }}
    >
      <div className="sn-modal sn-modal--wide">
        {/* ── Header ── */}
        <div className="sn-header">
          <div>
            <h2 className="sn-title">Wysyłka kontaktów</h2>
            <span className="sn-subtitle">
              {fmtPLN(sendSum)} netto · {props.sendOption}
            </span>
          </div>
          <button className="ld-close" onClick={props.close}>
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="sn-body">
          {loading ? (
            <Loading />
          ) : (
            <>
              {/* Client cards */}
              <div className="sld-clients">
                {sendTable.map((client) => (
                  <div key={client.id} className="sld-client">
                    <div className="sld-client-header">
                      <div className="sld-client-info">
                        <span className="sld-client-name">{client.name}</span>
                        <span className="sld-client-mail">{client.mail}</span>
                      </div>
                      <span className="sld-client-value">
                        {fmtPLN(client.price * client.leads.length)}
                      </span>
                    </div>

                    {/* Valid leads */}
                    {client.leads.length > 0 ? (
                      <div className="sld-leads">
                        {client.leads.map((lead) => (
                          <div key={lead.id} className="sld-lead">
                            {lead.name}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="sld-empty">Brak leadów do wysyłki</div>
                    )}

                    {/* Duplicates */}
                    {client.leadsNon.length > 0 && (
                      <div className="sld-dupes">
                        <div className="sld-dupes-header">
                          <AlertTriangle size={13} /> Usunięte z wysyłki —
                          duplikaty
                        </div>
                        {client.leadsNon.map((d, i) => (
                          <div key={i} className="sld-dupe">
                            {d.lead.name}{" "}
                            <span className="sld-dupe-date">{d.assDate}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="sld-footer">
                <label className="sld-check-label">
                  <input
                    type="checkbox"
                    className="db-check"
                    checked={sendMail}
                    onChange={() => setSendMail(!sendMail)}
                  />
                  <Mail size={14} /> Wyślij wiadomość do kontaktów
                </label>

                {hasLeadsToSend && (
                  <button
                    className="lq-btn lq-btn--success sld-send-btn"
                    onClick={handleSend}
                  >
                    <Send size={15} /> Wyślij kontakty
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendLeadsDetails;
